
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, FileDown, Code } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { toast } from 'sonner';

interface FullReportDisplayProps {
  pageAnalysisResult: DeepAnalysisResult;
}

export const FullReportDisplay: React.FC<FullReportDisplayProps> = ({
  pageAnalysisResult
}) => {
  const [copiedState, setCopiedState] = useState(false);

  // Copy JSON to clipboard with visual feedback
  const copyJsonToClipboard = () => {
    const json = formatJsonForDisplay();
    navigator.clipboard.writeText(json).then(() => {
      setCopiedState(true);
      toast.success('JSON copied to clipboard');
      setTimeout(() => {
        setCopiedState(false);
      }, 1500);
    }).catch(err => {
      console.error('Failed to copy JSON:', err);
      toast.error('Failed to copy to clipboard');
    });
  };

  // Download JSON report
  const downloadJsonReport = () => {
    const json = JSON.stringify(pageAnalysisResult, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'analysis-report.json');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('JSON report downloaded');
  };
  
  // Format the JSON for display
  const formatJsonForDisplay = () => {
    try {
      return JSON.stringify(pageAnalysisResult, null, 2);
    } catch (error) {
      console.error('Error formatting JSON:', error);
      return 'Error formatting JSON data';
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between items-center">
        <h4 className="text-md font-medium">Full Report</h4>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={downloadJsonReport}
            className="transition-all"
          >
            <FileDown className="h-4 w-4 mr-1" />
            Download JSON
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={copyJsonToClipboard}
            className={`transition-all ${copiedState ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400' : ''}`}
          >
            {copiedState ? (
              <Check className="h-4 w-4 mr-1 animate-scale" />
            ) : (
              <Code className="h-4 w-4 mr-1" />
            )}
            Copy JSON
          </Button>
        </div>
      </div>
      
      <div className="mt-4 animate-fade-in">
        <div className="relative">
          <Textarea
            className="min-h-[400px] font-mono text-sm"
            readOnly
            value={formatJsonForDisplay()}
          />
          <Button
            size="sm"
            variant="secondary"
            className={`absolute top-2 right-2 transition-all ${copiedState ? 'bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400' : ''}`}
            onClick={copyJsonToClipboard}
          >
            {copiedState ? (
              <Check className="h-4 w-4 mr-1 animate-scale" />
            ) : (
              <Copy className="h-4 w-4 mr-1" />
            )}
            Copy
          </Button>
        </div>
      </div>
    </div>
  );
};
